import { NetworkStatus } from '@apollo/client';

import { variablesAtLoadedDepth } from '@utils/loadedPageDepth';

import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

/**
 * Refetches every active query with one of `queryNames`, each as its own
 * network request — never joined onto an identical request already in flight.
 *
 * The plain `refetchQueries: ['listTasks']` a mutation offers goes through
 * Apollo's query deduplication: if an earlier refetch of the same query and
 * variables is still waiting on the server, the new one piggybacks on it, and
 * Apollo writes that earlier response to the cache under the new request's id
 * (the "ignore older responses" guard only compares ids, not payloads). After
 * quick successive toggles — complete A, complete B, reopen both, complete
 * both — the refetch fired by the last toggle can therefore resolve with a
 * snapshot taken before that toggle reached the server, and the tasks snap
 * back to their old state a few seconds later while the server holds the new
 * one (TA-11018 QA fail).
 *
 * Turning deduplication off for these refetches gives each one a fresh
 * request id, so a slower earlier response is discarded and only the last
 * refetch — issued after the last mutation settled — reaches the cache.
 *
 * Like `ObservableQuery.refetch()`, it is a disposable fetch that leaves the
 * query's own options untouched and keeps `no-cache` as `no-cache`. Unlike
 * it, it does not reset the query's last cache write (`refetch()` forces the
 * write even when the payload equals the previous one) and does not pause
 * polling for the fetch — check both before reusing this where either matters.
 *
 * A query paginated with `fetchMore` is re-requested at the depth it has
 * loaded (variablesAtLoadedDepth); with its one-page variables the refetch
 * would replace every loaded page with page 1 (TA-11016).
 */
export const refetchActiveQueriesFresh = (
  client: ApolloClient<NormalizedCacheObject>,
  queryNames: readonly string[]
): Promise<unknown> => {
  const names = new Set(queryNames);
  const refetches: Promise<unknown>[] = [];
  client.getObservableQueries('active').forEach((observableQuery) => {
    if (!observableQuery.queryName || !names.has(observableQuery.queryName)) {
      return;
    }
    const { fetchPolicy, context } = observableQuery.options;
    refetches.push(
      observableQuery.reobserve(
        {
          fetchPolicy: fetchPolicy === 'no-cache' ? 'no-cache' : 'network-only',
          context: { ...context, queryDeduplication: false },
          variables: variablesAtLoadedDepth(observableQuery),
        },
        NetworkStatus.refetch
      )
    );
  });
  // Failures already reach the user through errorLink; a refetch is a
  // best-effort reconcile, so one failing must not become an unhandled
  // rejection in the caller.
  return Promise.allSettled(refetches);
};
